import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getFollowing, setFollow } from "../data/userService";

const SessionContext = createContext(null);

/**
 * Logs who is signed in, and who they follow.
 * `dataVersion` is incremented with every write to ensure pages refetch fresh
 * data.
 * 
 * @param {*} param0 
 * @returns 
 */
const SessionProvider = ({ currentUid, children }) => {
  const [following, setFollowing] = useState(() => new Set());
  const [dataVersion, setDataVersion] = useState(0);

  useEffect(() => {
    if (!currentUid) {
      setFollowing(new Set());
      return undefined;
    };
    let live = true;
    getFollowing(currentUid).then((ids) => {
      if (live) setFollowing(new Set(ids));
    });
    return () => { live = false; };
  }, [currentUid]);

  const refresh = useCallback(() => setDataVersion((v) => v + 1), []);

  const toggleFollow = useCallback(
    async (targetUid) => {
      const shouldFollow = !following.has(targetUid);
      // Optimistic update: the button flips straight away
      setFollowing((prev) => {
        const next = new Set(prev);
        if (shouldFollow) next.add(targetUid); else next.delete(targetUid);
        return next;
      });
      await setFollow(currentUid, targetUid, shouldFollow);
      refresh();
    },
    [currentUid, following, refresh]
  );

  const value = useMemo(
    () => ({
      currentUid,
      following,
      isFollowing: (uid) => following.has(uid),
      toggleFollow,
      dataVersion,
      refresh,
    }),
    [
      currentUid,
      following,
      toggleFollow,
      dataVersion,
      refresh,
    ],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside <SessionProvider>");
  return context;
};

export { SessionProvider, useSession };
